'use client'

/**
 * Apple-compliant AI consent modal (Guideline 5.1.1(i) / 5.1.2(i)).
 */

import React from 'react'
import { ModalDialog } from '@/components/ui/ModalDialog'
import { BrainCircuit } from 'lucide-react'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import {
  WagamourV2ModalHeadline,
  WagamourV2ModalIconWell,
} from '@/components/v2/WagamourV2ModalContent'
import { v2ModalDividerClassName, v2ModalStyles } from '@/lib/design/v2ModalContent'

export interface AIConsentModalProps {
  isOpen: boolean
  onAgree: () => void
  onDecline: () => void
  onLearnMore?: () => void
}

const DATA_ITEMS = [
  'Dog profile information',
  'Breed, age, size, temperament, sensitivities, activity level, and behavior notes',
  'Location context',
  'Environmental conditions',
  'Weather and safety signals',
  'User inputs and requests',
  'Product recommendation context',
  'App activity related to your request',
]

export function AIConsentModal({
  isOpen,
  onAgree,
  onDecline,
  onLearnMore,
}: AIConsentModalProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onDecline} visualVariant="v2">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <WagamourV2ModalIconWell className="h-10 w-10">
            <BrainCircuit className="h-5 w-5" />
          </WagamourV2ModalIconWell>
          <div>
            <WagamourV2ModalHeadline>AI-Powered Personalization</WagamourV2ModalHeadline>
          </div>
        </div>

        <div className="space-y-3 text-sm leading-relaxed" style={v2ModalStyles.body}>
          <p>
            Wagamour uses <strong>Google Vertex AI / Gemini</strong> to provide personalized dog
            mobility guidance, Park Intelligence, AI Fit recommendations, product commentary, and
            other AI-powered insights.
          </p>
          <p>
            For reliability and service continuity, Wagamour may also use{' '}
            <strong>Anthropic Claude</strong> as a fallback AI provider.
          </p>
          <p>To provide these features, Wagamour may securely process relevant information including:</p>
          <ul className="ml-1 list-inside list-disc space-y-1" style={v2ModalStyles.muted}>
            {DATA_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p style={v2ModalStyles.muted}>Your information is not sold.</p>
          <p
            className={`text-xs ${v2ModalDividerClassName}`}
            style={{ ...v2ModalStyles.muted, ...v2ModalStyles.divider }}
          >
            AI-generated insights are suggestions only and are not veterinary, medical, safety, or
            professional advice.
          </p>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <WagamourV2Button type="button" fullWidth onClick={onAgree}>
            I Agree
          </WagamourV2Button>
          <WagamourV2Button type="button" variant="secondary" fullWidth onClick={onDecline}>
            Not Now
          </WagamourV2Button>
          {onLearnMore && (
            <button
              type="button"
              onClick={onLearnMore}
              className="w-full py-2 text-sm font-medium transition-opacity hover:opacity-90"
              style={v2ModalStyles.link}
            >
              Learn More
            </button>
          )}
        </div>
      </div>
    </ModalDialog>
  )
}
